<template>
  <div class="row">
    <div class="col-12 text-muted mb-3" data-cy="form_budget_picker_campaign_budget_help">
      <!-- TODO replace with translation -->
      <small>{{
        $t('orders.wizard.analytics.budget_help', { duration: numberOfDays, min_budget: minimumTotalBudgetFormatted })
      }}</small>
    </div>
    <div class="col-6" data-cy="form_budget_picker_campaign_total_budget">
      <LiFormInput
        v-model.number="modelValue"
        type="currency"
        :label="$t('total_budget')"
        :is-required="true"
        class="col-12"
        :input-class="totalBudgetErrors.length ? 'is-invalid' : ''"
        :locale="defaultLocale"
        :currency="currency"
        :errors="totalBudgetErrors">
      </LiFormInput>
    </div>
    <div class="col-6" data-cy="form_budget_picker_campaign_daily_budget">
      <LiFormInput
        v-model.number="internalDaily"
        type="currency"
        :label="$t('daily_budget')"
        :is-required="true"
        class="col-12"
        :currency="currency"
        :locale="defaultLocale"
        :input-class="dailyBudgetErrors.length ? 'is-invalid' : ''"
        :errors="dailyBudgetErrors"
        :key="dailyRerenderKey">
      </LiFormInput>
    </div>
    <div
      class="col-12 mt-2"
      data-cy="form_budget_picker_campaign_budget_slider"
      :key="'s-' + minimumDailyBudget + maximumDailyBudget">
      <input
        v-model.number="internalDaily"
        type="range"
        class="form-range mb-2"
        :min="minimumDailyBudget"
        :max="maximumDailyBudget"
        step="1" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatCurrency } from '@/locale/format';
import { computed, watch, ref } from 'vue';
import type { WritableComputedRef } from 'vue';

import { parseISO } from 'date-fns';

import { FormInput as LiFormInput } from '@prospective/lithium';

import { defaultCurrency, defaultLocale } from '../../config/app';
import type { IProductLimitations } from '@/types/product';

const modelValue = defineModel<number>({ required: true, default: 0 });

const dailyRerenderKey = ref(0);

const props = defineProps<{
  limitations: IProductLimitations;
  startdate: string;
  enddate: string;
  currency?: string;
}>();

const emit = defineEmits<{ (e: 'invalidBudget', value: boolean): void }>();

// calculate the number of days available
const numberOfDays = computed(() => {
  const start = parseISO(props.startdate);
  const end = parseISO(props.enddate);

  const timeDiffrence = end.getTime() - start.getTime();
  if (timeDiffrence <= 0) return 1;

  return Math.round((end.getTime() - start.getTime()) / (1000 * 3600 * 24));
});

const internalDaily: WritableComputedRef<number> = computed({
  get(): number {
    return Math.floor((modelValue.value ?? 0) / Math.max(1, numberOfDays.value));
  },
  set(value: number): void {
    modelValue.value = value * numberOfDays.value;
    dailyRerenderKey.value += 1;
  },
});

// inital default to 50
if (internalDaily.value < 1) {
  internalDaily.value = props.limitations.budget_min || 0;
  dailyRerenderKey.value += 1;
}

const minimumDailyBudget = computed(() => {
  // calculate the actual minimum daily budget
  // which is the minimum total budget divided by the number of days against the minimum daily budget
  const minimum = (props.limitations.budget_min || 0) / numberOfDays.value;
  return Math.floor(Math.max(minimum, props.limitations.budget_daily_min || 0));
});

const minimumTotalBudget = computed(() => {
  return minimumDailyBudget.value * numberOfDays.value;
});

const maximumDailyBudget = computed(() => {
  const maximum = props.limitations.budget_max ? props.limitations.budget_max / numberOfDays.value : Infinity;
  return Math.floor(Math.min(maximum, props.limitations.budget_daily_max || Infinity));
});

const maximumTotalBudget = computed(() => {
  return maximumDailyBudget.value * numberOfDays.value;
});

const minimumTotalBudgetFormatted = computed(() => {
  return (minimumTotalBudget.value / 100).toLocaleString(defaultLocale, {
    style: 'currency',
    currency: props.currency ?? defaultCurrency,
  });
});

const totalBudgetErrors = computed(() => {
  const errors: string[] = [];
  if (modelValue.value < minimumTotalBudget.value) {
    errors.push(`The minimum is ${minimumTotalBudgetFormatted.value}`);
  } else if (modelValue.value > maximumTotalBudget.value) {
    errors.push(`The maximum is ${formatCurrency(maximumTotalBudget.value, props.currency ?? defaultCurrency)}`);
  }
  return errors;
});

const dailyBudgetErrors = computed(() => {
  const errors: string[] = [];
  if (internalDaily.value < minimumDailyBudget.value) {
    errors.push(`The minimum is ${formatCurrency(minimumDailyBudget.value, props.currency ?? defaultCurrency)}`);
  } else if (internalDaily.value > maximumDailyBudget.value) {
    errors.push(`The maximum is ${formatCurrency(maximumDailyBudget.value, props.currency ?? defaultCurrency)}`);
  }
  return errors;
});

watch(
  [totalBudgetErrors, dailyBudgetErrors],
  () => {
    if (totalBudgetErrors.value.length || dailyBudgetErrors.value.length) emit('invalidBudget', true);
    else emit('invalidBudget', false);
  },
  { immediate: true }
);
</script>
